import { useMemo, useRef, useState } from "react";

import {
  HeatingNetworkType,
  type Building,
  type Floor,
  type Gateway,
  type Room,
  type Valve,
} from "@eisox/api/core-api";
import { ActionButton, Button, Loader, Modal, Plan } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PencilIcon, ValveIcon } from "@eisox/icons";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { heatingNetworkApi, valveApi } from "~/api";
import { ExpandedGateway } from "~/components/expended-gateway";
import { ExpandedRoom } from "~/components/expended-room";
import { Valve as ValveMarker } from "~/components/valve";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { formatMacInput, macInputMaxLength } from "~/helpers/mac-input.helper";
import { m } from "~/paraglide/messages";

import {
  emptyValveForm,
  nextValveUid,
  plannedValveToStaged,
  stagedToCreateBody,
  stagedToUpdateBody,
  valveDiffers,
  valveFormSchema,
  type StagedValve,
  type ValveFormValues,
  type ValveMode,
} from "../helpers/add-valve.helper";

interface ValveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ValveMode;
  building: Building;
  floors: Floor[];
  floorId: string;
  valves: Valve[];
  rooms: Room[];
  gateways: Gateway[];
}

export function ValveDialog({
  open,
  onOpenChange,
  mode,
  building,
  floors,
  floorId,
  valves,
  rooms,
  gateways,
}: ValveDialogProps) {
  const queryClient = useQueryClient();

  // Pre-install loads the existing planned (no-mac) valves so the dialog opens as a set-editor. Only these
  // are editable/removable; installed (with-mac) valves are read-only context.
  const [originalById] = useState<Map<string, StagedValve>>(() => {
    const map = new Map<string, StagedValve>();
    if (mode === "preinstall") {
      valves.filter(v => !v.mac).forEach(v => map.set(v.id, plannedValveToStaged(v)));
    }
    return map;
  });
  const [staged, setStaged] = useState<StagedValve[]>(() => [...originalById.values()]);

  const localIdRef = useRef(1);
  const nextLocalId = () => String(localIdRef.current++);
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);

  const existingMacs = useMemo(() => valves.map(v => v.mac).filter((mac): mac is string => !!mac), [valves]);
  // Persisted valves not adopted into `staged` (read-only installed context) drive the next free uid.
  const contextValves = valves.filter(v => !originalById.has(v.id));
  const minUid = nextValveUid(contextValves, staged);

  const takenMacsRef = useRef<string[]>([]);
  takenMacsRef.current = [...existingMacs, ...staged.map(s => s.mac).filter(Boolean)];

  const { data: heatingNetworks = [] } = useQuery(heatingNetworkApi.queries.building(building.id));

  const schema = useMemo(
    () =>
      valveFormSchema(
        mode,
        () => takenMacsRef.current,
        staged,
        contextValves.map(v => v.uid),
      ),
    [contextValves, mode, staged],
  );
  // Add mode fans out one POST per valve (create-only). Pre-install sends the whole change-set to one
  // atomic reconcile endpoint so a dropped request can't half-apply the renumbering.
  const createMutation = useMutation({
    meta: { toast: { action: "create" } },
    mutationFn: (body: Parameters<typeof valveApi.create>[1]) => valveApi.create(building.id, body),
  });
  const reconcileMutation = useMutation({
    mutationFn: (body: Parameters<typeof valveApi.reconcile>[1]) => valveApi.reconcile(building.id, body),
  });

  const [initialId] = useState(nextLocalId);
  // Stable defaultValues: `useForm` re-applies them to an untouched form every render (would clobber resets).
  const [defaultValues] = useState(() => emptyValveForm(initialId, floorId, minUid));
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: schema },
    onSubmit: () => {},
  });

  const currentId = useSelector(form.store, s => s.values.id);
  const currentFloorId = useSelector(form.store, s => s.values.floorId);
  const currentRoomId = useSelector(form.store, s => s.values.roomId);
  const currentGatewayId = useSelector(form.store, s => s.values.gatewayId);

  const isEditing = staged.some(s => s.id === currentId);

  const {
    data: plan,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = useQuery({
    ...floorPlanQueries.byId(currentFloorId),
    enabled: !!currentFloorId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const floorOptions = floors.map(f => ({ value: f.id, name: f.name }));
  const roomOptions = rooms.map(r => ({ value: r.id, name: r.name }));
  const gatewayOptions = gateways.map(g => ({ value: g.id, name: `${g.uid} - ${g.name}` }));
  const heatingNetworkOptions = heatingNetworks
    .filter(h => h.type === HeatingNetworkType.RADIATOR)
    .map(h => ({ value: h.id, name: h.name }));

  const stagedServerIds = new Set(staged.map(s => s.serverId).filter((id): id is string => !!id));
  // Plan tags: rooms and gateways on the selected floor are clickable to link the valve; installed valves are context.
  const roomsOnFloor = rooms.filter(r => r.floor.id === currentFloorId);
  const gatewaysOnFloor = gateways.filter(g => g.floor.id === currentFloorId);
  const existingValvesOnFloor = valves.filter(v => v.floor.id === currentFloorId && !originalById.has(v.id));
  const stagedOnFloor = staged.filter(s => s.floorId === currentFloorId);

  const newValves = staged.filter(s => !s.serverId);
  const changedValves = staged.filter((s): s is StagedValve & { serverId: string } => {
    if (!s.serverId) return false;
    const original = originalById.get(s.serverId);
    return original != null && valveDiffers(s, original);
  });
  const removedIds = [...originalById.keys()].filter(id => !stagedServerIds.has(id));
  const hasChanges = newValves.length > 0 || changedValves.length > 0 || removedIds.length > 0;

  const resetForm = (values: ValveFormValues) => form.reset(values, { keepDefaultValues: true });

  const startNewValve = (nextUid: number) =>
    resetForm(emptyValveForm(nextLocalId(), form.state.values.floorId, nextUid));

  const withUid = (valve: StagedValve, newUid: number): StagedValve => ({ ...valve, uid: newUid });

  // Move the valve at `fromUid` to `toUid`, shifting the ones in between (legacy reOrderItemsUid).
  const reorderUid = (list: StagedValve[], fromUid: number, toUid: number): StagedValve[] =>
    list.map(v => {
      if (v.uid === fromUid) return withUid(v, toUid);
      if (fromUid > toUid && v.uid >= toUid && v.uid < fromUid) return withUid(v, v.uid + 1);
      if (fromUid < toUid && v.uid > fromUid && v.uid <= toUid) return withUid(v, v.uid - 1);
      return v;
    });

  const placeOnPlan = async ({ x, y }: { x: number; y: number }) => {
    const v = { ...form.state.values, x, y };
    // A click while a placed valve is selected starts a fresh valve (deselect); repositioning is drag-only.
    if (stagedRef.current.some(s => s.id === v.id)) {
      startNewValve(nextValveUid(contextValves, stagedRef.current));
      return;
    }
    if (!schema.safeParse(v).success) {
      form.setFieldValue("x", x);
      form.setFieldValue("y", y);
      await form.handleSubmit(); // surface field errors (missing room/gateway/serial)
      return;
    }
    setStaged(prev => [...prev, v]);
    // Advance to a fresh valve, inheriting floor/room/gateway/heating-network for quick sequential placement.
    resetForm({
      ...emptyValveForm(nextLocalId(), v.floorId, v.uid + 1),
      roomId: v.roomId,
      gatewayId: v.gatewayId,
      heatingNetworkId: v.heatingNetworkId,
    });
  };

  const editStaged = (valve: StagedValve) => {
    const { serverId: _serverId, ...values } = valve;
    resetForm(values);
  };

  const applyEdit = async () => {
    const v = form.state.values;
    if (!schema.safeParse(v).success) {
      await form.handleSubmit();
      return;
    }
    const oldUid = staged.find(s => s.id === v.id)?.uid;
    setStaged(prev => {
      let next = prev.map(s => (s.id === v.id ? withUid({ ...s, ...v }, oldUid ?? v.uid) : s));
      if (oldUid != null && v.uid !== oldUid) {
        next = prev.some(s => s.id !== v.id && s.uid === v.uid)
          ? reorderUid(next, oldUid, v.uid)
          : next.map(s => (s.id === v.id ? withUid(s, v.uid) : s));
      }
      return next;
    });
    startNewValve(nextValveUid(contextValves, stagedRef.current));
  };
  const cancelEdit = () => {
    const original = staged.find(s => s.id === currentId);
    if (original) editStaged(original);
  };

  const deleteStaged = () => {
    const target = staged.find(s => s.id === currentId);
    if (!target) return;
    // Renumber: every valve above the removed uid drops by one (legacy handleDeleteValve).
    const remaining = staged.filter(s => s.id !== target.id).map(s => (s.uid > target.uid ? withUid(s, s.uid - 1) : s));
    setStaged(remaining);
    resetForm(emptyValveForm(nextLocalId(), form.state.values.floorId, nextValveUid(contextValves, remaining)));
  };

  const handleSaveAll = async () => {
    if (mode === "install") {
      await Promise.all(newValves.map(s => createMutation.mutateAsync(stagedToCreateBody(s, mode))));
    } else {
      await reconcileMutation.mutateAsync({
        create: newValves.map(s => stagedToCreateBody(s, mode)),
        update: changedValves.map(s => ({ id: s.serverId, ...stagedToUpdateBody(s) })),
        delete: removedIds,
      });
    }
    await queryClient.invalidateQueries({ queryKey: valveApi.queries.building(building.id).queryKey });
    onOpenChange(false);
  };

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      <Modal.Content className="flex h-[85dvh] w-[95vw] flex-col">
        <form onSubmit={e => e.preventDefault()}>
          <Modal.Header
            title={mode === "install" ? m.valve_add_title() : m.valve_preinstall_title()}
            subtitle={m.valve_add_subtitle()}
            icon={<ValveIcon />}
          >
            <Modal.Close asChild>
              <ActionButton variant="cancel">{m.cancel()}</ActionButton>
            </Modal.Close>
            <ActionButton
              type="button"
              variant="primary"
              rounded
              disabled={!hasChanges}
              onClick={() => void handleSaveAll()}
            >
              {m.save()} <PencilIcon />
            </ActionButton>
          </Modal.Header>
        </form>

        <div className="flex h-full gap-[40px] overflow-auto">
          <form.AppForm>
            <div className="flex h-full flex-[3] flex-col gap-[10px] overflow-y-auto">
              <h3 className="font-semibold text-2xl">{isEditing ? m.valve() : m.valve_add_new_box()}</h3>

              <form.AppField name="floorId">
                {field => (
                  <field.Select
                    label={m.level()}
                    placeholder={m.select_level()}
                    multiple={false}
                    options={floorOptions}
                    // Changing floor resets the room (rooms are floor-specific); the default handler sets floorId.
                    onValueChange={() => form.setFieldValue("roomId", "")}
                    classNames={{ root: "w-full", control: "w-full" }}
                  />
                )}
              </form.AppField>

              <form.AppField name="uid">
                {field => <field.NumberField label={m.number()} classNames={{ root: "w-full", control: "w-full" }} />}
              </form.AppField>

              {mode === "install" && (
                <form.AppField name="mac">
                  {field => (
                    <field.TextField
                      label={m.serial_number()}
                      placeholder={m.serial_number_placeholder()}
                      classNames={{ root: "w-full", control: "w-full" }}
                      maxLength={macInputMaxLength("valve")}
                      onChange={e => form.setFieldValue("mac", formatMacInput(e.target.value, "valve"))}
                    />
                  )}
                </form.AppField>
              )}

              <form.AppField name="roomId">
                {field => (
                  <field.Select
                    label={m.room()}
                    placeholder={m.select_room()}
                    multiple={false}
                    options={roomOptions}
                    classNames={{ root: "w-full", control: "w-full" }}
                  />
                )}
              </form.AppField>

              <form.AppField name="gatewayId">
                {field => (
                  <field.Select
                    label={m.gateway()}
                    placeholder={m.select_gateway()}
                    multiple={false}
                    options={gatewayOptions}
                    classNames={{ root: "w-full", control: "w-full" }}
                  />
                )}
              </form.AppField>

              {heatingNetworkOptions.length > 0 && (
                <form.AppField name="heatingNetworkId">
                  {field => (
                    <field.Select
                      label={m.heating_network()}
                      placeholder={m.select_heating_network()}
                      multiple={false}
                      options={heatingNetworkOptions}
                      classNames={{ root: "w-full", control: "w-full" }}
                    />
                  )}
                </form.AppField>
              )}

              {isEditing && (
                <div className="flex items-center justify-between gap-[10px]">
                  <Button className="text-primary-red" onClick={deleteStaged}>
                    {m.delete()}
                  </Button>
                  <div className="flex gap-[10px]">
                    <Button className="text-primary-dark-gray" onClick={cancelEdit}>
                      {m.cancel()}
                    </Button>
                    <ActionButton
                      variant="primary"
                      onClick={() => {
                        void applyEdit();
                      }}
                    >
                      {m.edit()} <PencilIcon />
                    </ActionButton>
                  </div>
                </div>
              )}

              <div className="mb-0 mx-auto mt-auto text-base text-primary-dark-gray">{m.valve_add_info()}</div>
            </div>
          </form.AppForm>

          <div className="relative h-full flex-[7]">
            {isPlanLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader />
              </div>
            ) : plan ? (
              <Plan.Root
                onClickOnPlan={position => {
                  void placeOnPlan(position);
                }}
              >
                <Plan.Controls className="top-2.5 left-2.5 absolute z-1" />
                <Plan.Wrapper className="h-full! w-full!" plan={plan}>
                  {gatewaysOnFloor.map(g => (
                    <Plan.Item key={g.id} position={{ x: g.floor.x, y: g.floor.y }} origin="bottom center">
                      <ExpandedGateway
                        as="button"
                        selected={g.id === currentGatewayId}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          form.setFieldValue("gatewayId", g.id);
                        }}
                      >
                        {g.uid} - {g.name}
                      </ExpandedGateway>
                    </Plan.Item>
                  ))}
                  {roomsOnFloor.map(r => (
                    <Plan.Item key={r.id} position={{ x: r.floor.x, y: r.floor.y }} origin="bottom center">
                      <ExpandedRoom
                        as="button"
                        selected={r.id === currentRoomId}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          form.setFieldValue("roomId", r.id);
                        }}
                      >
                        {r.name}
                      </ExpandedRoom>
                    </Plan.Item>
                  ))}
                  {existingValvesOnFloor.map(v => (
                    <Plan.Item key={v.id} position={{ x: v.floor.x, y: v.floor.y }}>
                      <ValveMarker disabled>{v.uid}</ValveMarker>
                    </Plan.Item>
                  ))}
                  {stagedOnFloor.map(s => (
                    <Plan.Item
                      key={s.id}
                      draggable
                      position={{ x: s.x, y: s.y }}
                      onPositionEnd={({ x, y }) =>
                        setStaged(prev => prev.map(p => (p.id === s.id ? { ...p, x, y } : p)))
                      }
                      onPointerDown={e => (pointerDownRef.current = { x: e.clientX, y: e.clientY })}
                      onPointerUp={e => {
                        const down = pointerDownRef.current;
                        if (down && Math.abs(e.clientX - down.x) < 5 && Math.abs(e.clientY - down.y) < 5) editStaged(s);
                      }}
                    >
                      <ValveMarker backgroundColor="blue" selected={s.id === currentId}>
                        {s.uid}
                      </ValveMarker>
                    </Plan.Item>
                  ))}
                </Plan.Wrapper>
              </Plan.Root>
            ) : (
              <div className="flex h-full items-center justify-center text-primary-dark-blue/60 italic">
                {isPlanError ? m.plan_unavailable() : null}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
}

export function AddValveDialog(props: Omit<ValveDialogProps, "mode">) {
  return <ValveDialog {...props} mode="install" />;
}

export function PreinstallValveDialog(props: Omit<ValveDialogProps, "mode">) {
  return <ValveDialog {...props} mode="preinstall" />;
}
